import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    Box,
    Alert,
    Typography,
    Chip,
    Avatar,
    Popover,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    Modal,
    Skeleton,
    TextField,
    InputAdornment,
    Grid,
    useMediaQuery,
    useTheme,
    LinearProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useGetGoodsMutation } from '../../store/api/goodsApi';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setIsExporting, setSelectedRows } from '../../store/slices/uiSlice';
import { setSearch } from '../../store/slices/filtersSlice';
import { addToShoppingList, removeFromShoppingList } from '../../store/slices/shoppingListSlice';
import { exportToExcel } from '../../utils/excelExport';
import { GoodsItem, ApiResponse } from '../../types/types';
import Pagination from './Pagination';
import { themeColors, semanticColors } from '../../theme/colors';
import GearLoader from '../Common/GearLoader';
import ShareButton from '../Common/ShareButton';

// Типы для работы с категориями
interface GoodsItemWithCategory extends GoodsItem {
    isFirstInCategory?: boolean;
    categoryName?: string;
    categoryItemsCount?: number;
}

// Функция для извлечения категории из товара
const extractCategory = (item: GoodsItem): string => {
    // Если есть поле category, используем его
    if (item.category) {
        return item.category;
    }

    // Иначе пытаемся извлечь категорию из названия товара
    const title = item.title.toLowerCase();

    if (title.includes('macbook') || title.includes('макбук')) return 'MacBook';
    if (title.includes('iphone') || title.includes('айфон')) return 'iPhone';
    if (title.includes('ipad') || title.includes('айпад')) return 'iPad';
    if (title.includes('apple watch') || title.includes('эппл вотч')) return 'Apple Watch';
    if (title.includes('airpods') || title.includes('эйрподс')) return 'AirPods';
    if (title.includes('keyboard') || title.includes('клавиатура')) return 'Apple Keyboard';
    if (title.includes('mouse') || title.includes('мышь')) return 'Apple Mouse';
    if (title.includes('pencil') || title.includes('пенсил')) return 'Apple Pencil';
    if (title.includes('magic')) return 'Apple Magic';
    if (title.includes('чехол') || title.includes('case')) return 'Чехлы и аксессуары';
    if (title.includes('стекло') || title.includes('glass')) return 'Защитные стекла';
    if (title.includes('зарядка') || title.includes('charger') || title.includes('кабель')) return 'Зарядки и кабели';

    return 'Общая';
};

// Функции для работы с категориями
const groupAndSortByCategory = (items: GoodsItem[]) => {
    const grouped: { [key: string]: GoodsItem[] } = {};

    items.forEach(item => {
        // Используем функцию извлечения категории
        const category = extractCategory(item);
        if (!grouped[category]) {
            grouped[category] = [];
        }
        grouped[category].push(item);
    });

    // Сортируем категории по алфавиту
    const sortedCategories = Object.keys(grouped).sort((a, b) => a.localeCompare(b, 'ru'));

    // Сортируем товары внутри каждой категории по названию
    sortedCategories.forEach(category => {
        grouped[category].sort((a, b) => a.title.localeCompare(b.title, 'ru'));
    });

    return { grouped, sortedCategories };
};

const createFlatArrayWithCategories = (grouped: { [key: string]: GoodsItem[] }, sortedCategories: string[]): GoodsItemWithCategory[] => {
    const flatArray: GoodsItemWithCategory[] = [];

    sortedCategories.forEach(categoryName => {
        const categoryItems = grouped[categoryName];
        categoryItems.forEach((item, index) => {
            flatArray.push({
                ...item,
                isFirstInCategory: index === 0,
                categoryName: categoryName,
                categoryItemsCount: categoryItems.length
            });
        });
    });

    return flatArray;
};

// Компонент заголовка категории
const CategoryHeader: React.FC<{
    categoryName: string;
    itemsCount: number;
    expanded: boolean;
    onToggle: () => void;
    selectedCount: number;
}> = ({ categoryName, itemsCount, expanded, onToggle, selectedCount }) => (
    <TableRow
        sx={{
            backgroundColor: semanticColors.glass.background,
            borderTop: `2px solid ${themeColors['300']}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            '&:hover': {
                backgroundColor: `${themeColors['50']}`,
            },
        }}
    >
        <TableCell colSpan={8} sx={{ py: 1.5 }}>
            <Box
                onClick={onToggle}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    gap: 1
                }}
            >
                {expanded ? (
                    <ExpandLessIcon sx={{ color: themeColors['600'] }} />
                ) : (
                    <ExpandMoreIcon sx={{ color: themeColors['600'] }} />
                )}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        color: themeColors['800'],
                        fontSize: '1rem'
                    }}
                >
                    {categoryName}
                </Typography>
                <Chip
                    label={`${itemsCount} товаров`}
                    size="small"
                    sx={{
                        backgroundColor: themeColors['100'],
                        color: themeColors['700'],
                        fontSize: '0.75rem',
                        height: 24,
                    }}
                />
                {selectedCount > 0 && (
                    <Chip
                        label={`${selectedCount} выбрано`}
                        size="small"
                        sx={{
                            backgroundColor: themeColors['200'],
                            color: themeColors['800'],
                            fontSize: '0.75rem',
                            height: 24,
                        }}
                    />
                )}
            </Box>
        </TableCell>
    </TableRow>
);

// Мемоизированный компонент строки таблицы
const TableRowMemoized = React.memo<{
    row: GoodsItemWithCategory;
    isSelected: boolean;
    isInShoppingList: boolean;
    onSelect: (id: number) => void;
    onStockClick: (event: React.MouseEvent<HTMLElement>, warehouses: GoodsItem['warehouses']) => void;
    onImageClick: (imageUrl: string) => void;
    onToggleShoppingList: (item: GoodsItem) => void;
    calculateTotalStock: (warehouses: GoodsItem['warehouses']) => number;
}>(({ row, isSelected, isInShoppingList, onSelect, onStockClick, onImageClick, onToggleShoppingList, calculateTotalStock }) => {
    const totalStock = useMemo(() => calculateTotalStock(row.warehouses), [row.warehouses, calculateTotalStock]);

    return (
        <TableRow
            hover
            selected={isSelected}
            sx={{
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                },
                ...(isInShoppingList && {
                    backgroundColor: 'rgba(54, 122, 186, 0.04)',
                }),
            }}
        >
            <TableCell padding="checkbox">
                <Checkbox
                    checked={isSelected}
                    onChange={() => onSelect(row.id)}
                    sx={{ color: themeColors['500'] }}
                />
            </TableCell>
            <TableCell>
                {row.image ? (
                    <Avatar
                        src={row.image}
                        variant="rounded"
                        onClick={() => onImageClick(row.image)}
                        sx={{
                            borderRadius: 1,
                            width: 50,
                            height: 50,
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8,
                            },
                        }}
                    >
                        <ImageIcon />
                    </Avatar>
                ) : (
                    <Avatar
                        variant="rounded"
                        sx={{
                            borderRadius: 1,
                            width: 50,
                            height: 50,
                            bgcolor: themeColors['100'],
                            color: themeColors['400']
                        }}
                    >
                        <ImageIcon />
                    </Avatar>
                )}
            </TableCell>
            <TableCell sx={{ color: themeColors['700'], fontWeight: 500 }}>
                {row.kod}
            </TableCell>
            <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500, color: themeColors['900'] }}>
                    {row.title}
                </Typography>
                {row?.artikul && <>
                    <Typography variant="caption" color="text.secondary">
                        {row?.artikul}
                    </Typography>&nbsp;
                </>}
                {row.sub_title && (
                    <Typography variant="caption" color="text.secondary">
                        {row.sub_title}
                    </Typography>
                )}
                {isInShoppingList && (
                    <div>
                        <Chip
                            label="В списке покупок"
                            size="small"
                            sx={{
                                mt: 0.5,
                                backgroundColor: themeColors['100'],
                                color: themeColors['700'],
                                fontSize: '0.7rem',
                            }}
                        />
                    </div>
                )}
            </TableCell>
            <TableCell align="center">
                <Typography variant="body2" fontWeight="medium" sx={{ color: themeColors['600'] }}>
                    {row.price_3} {row.currency === 'rub' ? '₽' : '$'}
                </Typography>
            </TableCell>
            <TableCell align="center">
                <Typography variant="body2" fontWeight="medium" sx={{ color: themeColors['700'] }}>
                    {row.price_4} {row.currency === 'rub' ? '₽' : '$'}
                </Typography>
            </TableCell>
            <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Chip
                        label={totalStock}
                        size="small"
                        sx={{
                            backgroundColor: totalStock > 0 ? themeColors['100'] : 'transparent',
                            color: totalStock > 0 ? themeColors['700'] : themeColors['400'],
                            border: `1px solid ${totalStock > 0 ? themeColors['200'] : themeColors['200']}`,
                            fontWeight: 500,
                        }}
                    />
                    <Tooltip title="Подробнее по складам" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => onStockClick(e, row.warehouses)}
                            sx={{
                                color: themeColors['400'],
                                '&:hover': {
                                    backgroundColor: themeColors['50'],
                                },
                            }}
                        >
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </TableCell>
            <TableCell align="center">
                <Tooltip title={isInShoppingList ? "Убрать из списка заказа" : "Добавить в список заказа"} arrow>
                    <IconButton
                        onClick={() => onToggleShoppingList(row)}
                        size="small"
                        sx={{
                            color: isInShoppingList ? themeColors['600'] : themeColors['400'],
                            backgroundColor: isInShoppingList ? themeColors['50'] : 'transparent',
                            '&:hover': {
                                backgroundColor: isInShoppingList ? themeColors['100'] : themeColors['50'],
                            },
                        }}
                    >
                        {isInShoppingList ? (
                            <RemoveShoppingCartIcon fontSize="small" />
                        ) : (
                            <AddShoppingCartIcon fontSize="small" />
                        )}
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
});

const PriceListTable: React.FC = () => {
    const dispatch = useAppDispatch();
    const filters = useAppSelector((state) => state.filters);
    const { isExporting, selectedRows } = useAppSelector((state) => state.ui);
    const { items: shoppingListItems } = useAppSelector((state) => state.shoppingList);

    const [getGoods, { data, isLoading, error }] = useGetGoodsMutation();
    const [allSelected, setAllSelected] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedWarehouses, setSelectedWarehouses] = useState<GoodsItem['warehouses'] | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});

    // Состояние для прогрессивного рендеринга
    const [renderedItems, setRenderedItems] = useState<GoodsItem[]>([]);
    const [renderingProgress, setRenderingProgress] = useState(0);
    const [isRendering, setIsRendering] = useState(false);
    const renderingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const renderBatchSize = 100; // Размер пакета для рендеринга

    // Локальное состояние для поиска (без debouncer)
    const [searchValue, setSearchValue] = useState(filters.q);

    // Для определения мобильного устройства
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Храним предыдущие значения фильтров для сравнения
    const prevFiltersRef = useRef<string>();
    const isFirstLoadRef = useRef(true);

    // Функция прогрессивного рендеринга товаров
    const renderItemsProgressively = useCallback((items: GoodsItem[]) => {
        if (!items || items.length === 0) {
            setRenderedItems([]);
            setRenderingProgress(100);
            setIsRendering(false);
            return;
        }

        // Если товаров мало (до 200), рендерим сразу все
        if (items.length <= 200) {
            setRenderedItems(items);
            setRenderingProgress(100);
            setIsRendering(false);
            return;
        }

        // Для больших списков используем прогрессивный рендеринг
        setIsRendering(true);
        setRenderedItems([]);
        setRenderingProgress(0);

        let currentIndex = 0;
        const totalItems = items.length;

        const renderNextBatch = () => {
            const nextBatch = items.slice(currentIndex, currentIndex + renderBatchSize);

            setRenderedItems(prev => [...prev, ...nextBatch]);
            currentIndex += renderBatchSize;

            const progress = Math.min((currentIndex / totalItems) * 100, 100);
            setRenderingProgress(progress);

            if (currentIndex < totalItems) {
                // Используем requestAnimationFrame для плавного рендеринга
                renderingTimeoutRef.current = setTimeout(() => {
                    requestAnimationFrame(renderNextBatch);
                }, 16); // ~60fps
            } else {
                setIsRendering(false);
                setRenderingProgress(100);
            }
        };

        // Первая пачка рендерится сразу
        requestAnimationFrame(renderNextBatch);
    }, [renderBatchSize]);

    // Очистка таймаута при размонтировании
    useEffect(() => {
        return () => {
            if (renderingTimeoutRef.current) {
                clearTimeout(renderingTimeoutRef.current);
            }
        };
    }, []);

    // Запуск прогрессивного рендеринга при получении новых данных
    useEffect(() => {
        if (data?.items) {
            renderItemsProgressively(data.items);
        }
    }, [data?.items, renderItemsProgressively]);

    // Создаем Set из ID товаров в списке покупок для быстрого поиска
    const shoppingListItemIds = useMemo(() => {
        return new Set(shoppingListItems.map(item => item.id));
    }, [shoppingListItems]);

    // Обработка группировки по категориям с уже отрендеренными товарами
    const { groupedData, sortedCategories } = useMemo(() => {
        if (!renderedItems || renderedItems.length === 0) {
            return { groupedData: {}, sortedCategories: [] };
        }

        const { grouped, sortedCategories: categories } = groupAndSortByCategory(renderedItems);

        return {
            groupedData: grouped,
            sortedCategories: categories,
        };
    }, [renderedItems]);

    // Инициализируем expanded состояние для всех категорий (по умолчанию открыты)
    useEffect(() => {
        if (sortedCategories.length > 0) {
            const initialExpanded: { [key: string]: boolean } = {};
            sortedCategories.forEach(category => {
                if (!(category in expandedCategories)) {
                    initialExpanded[category] = true;
                }
            });
            if (Object.keys(initialExpanded).length > 0) {
                setExpandedCategories(prev => ({ ...prev, ...initialExpanded }));
            }
        }
    }, [sortedCategories]);

    // Синхронизируем локальное состояние поиска с глобальным
    useEffect(() => {
        setSearchValue(filters.q);
    }, [filters.q]);

    useEffect(() => {
        const currentFiltersStr = JSON.stringify({
            countOnPage: filters.countOnPage,
            page: filters.page,
            city: filters.city,
            q: filters.q,
            sort: filters.sort,
        });

        if (isFirstLoadRef.current || prevFiltersRef.current !== currentFiltersStr) {
            getGoods(filters);
            prevFiltersRef.current = currentFiltersStr;
            isFirstLoadRef.current = false;
        }
    }, [filters, getGoods]);

    const handleStockClick = useCallback((event: React.MouseEvent<HTMLElement>, warehouses: GoodsItem['warehouses']) => {
        setAnchorEl(event.currentTarget);
        setSelectedWarehouses(warehouses);
    }, []);

    const handleClosePopover = useCallback(() => {
        setAnchorEl(null);
        setSelectedWarehouses(null);
    }, []);

    const handleImageClick = useCallback((imageUrl: string) => {
        setPreviewImage(imageUrl);
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewImage(null);
    }, []);

    const calculateTotalStock = useCallback((warehouses: GoodsItem['warehouses']) => {
        return warehouses.reduce((total, warehouse) => total + warehouse.count, 0);
    }, []);

    // Функция поиска (вызывается по кнопке или Enter)
    const handleSearch = useCallback(() => {
        dispatch(setSearch(searchValue));
    }, [searchValue, dispatch]);

    // Обработка нажатия Enter в поле поиска
    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    }, [handleSearch]);

    const handleClearSearch = useCallback(() => {
        setSearchValue('');
        dispatch(setSearch(''));
    }, [dispatch]);

    // Функция добавления/удаления товара из списка заказа
    const handleToggleShoppingList = useCallback((item: GoodsItem) => {
        const isInList = shoppingListItemIds.has(item.id);
        if (isInList) {
            dispatch(removeFromShoppingList(item.id));
        } else {
            dispatch(addToShoppingList(item));
        }
    }, [dispatch, shoppingListItemIds]);

    // Функция переключения видимости категории
    const handleToggleCategory = useCallback((categoryName: string) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    }, []);

    const handleExport = useCallback(async () => {
        if (!data?.items) return;

        dispatch(setIsExporting(true));
        try {
            const dataToExport = selectedRows.length > 0
                ? data.items.filter(item => selectedRows.includes(item.id))
                : data.items;

            // Группируем и сортируем данные для экспорта
            const { grouped, sortedCategories: categories } = groupAndSortByCategory(dataToExport);

            const exportData: any[] = [];

            // Добавляем данные по категориям
            categories.forEach(categoryName => {
                // Добавляем товары категории
                grouped[categoryName].forEach(item => {
                    exportData.push({
                        'Категория': extractCategory(item), // Добавляем для группировки
                        'Артикул': item?.artikul || 'Отсутствует',
                        'Название': item.title,
                        'Опт': item.price_3,
                        'Опт-Мастер': item.price_4,
                    });
                });
            });

            await exportToExcel(exportData, {
                filename: `skynet_pricelist_by_categories_page_${filters.page}`,
                sheetName: 'Прайс-лист по категориям',
                includeHeaders: true,
                groupByCategory: true,
            });
        } catch (error) {
            console.error('Export error:', error);
            alert('Ошибка при экспорте данных');
        } finally {
            dispatch(setIsExporting(false));
        }
    }, [data?.items, selectedRows, dispatch, filters.page]);

    const handleSelectAll = useCallback(() => {
        if (!renderedItems) return;

        if (allSelected) {
            dispatch(setSelectedRows([]));
            setAllSelected(false);
        } else {
            dispatch(setSelectedRows(renderedItems.map(item => item.id)));
            setAllSelected(true);
        }
    }, [renderedItems, allSelected, dispatch]);

    const handleSelectRow = useCallback((id: number) => {
        const newSelected = selectedRows.includes(id)
            ? selectedRows.filter(rowId => rowId !== id)
            : [...selectedRows, id];

        dispatch(setSelectedRows(newSelected));
        setAllSelected(newSelected.length === renderedItems.length);
    }, [selectedRows, dispatch, renderedItems]);

    // Мемоизируем строки таблицы с категориями (работаем с отрендеренными товарами)
    const tableRows = useMemo(() => {
        if (!renderedItems || renderedItems.length === 0) return [];

        const rows: React.ReactNode[] = [];

        sortedCategories.forEach(categoryName => {
            const categoryItems = groupedData[categoryName] || [];
            const selectedInCategory = categoryItems.filter(item => selectedRows.includes(item.id)).length;

            // Добавляем заголовок категории
            rows.push(
                <CategoryHeader
                    key={`category-${categoryName}`}
                    categoryName={categoryName}
                    itemsCount={categoryItems.length}
                    expanded={expandedCategories[categoryName] ?? true}
                    onToggle={() => handleToggleCategory(categoryName)}
                    selectedCount={selectedInCategory}
                />
            );

            // Добавляем товары категории (если категория развернута)
            if (expandedCategories[categoryName] ?? true) {
                categoryItems.forEach((row) => {
                    rows.push(
                        <TableRowMemoized
                            key={row.id}
                            row={row}
                            isSelected={selectedRows.includes(row.id)}
                            isInShoppingList={shoppingListItemIds.has(row.id)}
                            onSelect={handleSelectRow}
                            onStockClick={handleStockClick}
                            onImageClick={handleImageClick}
                            onToggleShoppingList={handleToggleShoppingList}
                            calculateTotalStock={calculateTotalStock}
                        />
                    );
                });
            }
        });

        return rows;
    }, [renderedItems, sortedCategories, groupedData, selectedRows, shoppingListItemIds, expandedCategories, handleSelectRow, handleStockClick, handleImageClick, handleToggleShoppingList, calculateTotalStock, handleToggleCategory]);

    // Проверяем, применены ли какие-то фильтры для показа кнопки "Поделиться"  
    const hasActiveFilters = filters.q || filters.city !== 0 || filters.countOnPage !== 100;

    if (error) {
        return (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
                Ошибка загрузки данных. Попробуйте обновить страницу.
            </Alert>
        );
    }

    return (
        <Box>
            {/* Верхняя панель с поиском и кнопками */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 2,
                    backgroundColor: semanticColors.glass.background,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${semanticColors.border.light}`,
                }}
            >
                {/* Поиск и кнопки экспорта/поделиться */}
                <Grid container spacing={2} alignItems="center" flex='wrap' justifyContent='space-between' sx={{ mb: 2 }}>
                    {/* Поле поиска с кнопкой "Найти" */}
                    <Grid item xs={12} md={8} lg={9} display='flex' alignItems='center' gap={1}>
                        <TextField
                            fullWidth
                            size="medium"
                            placeholder="Введите название товара для поиска..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: themeColors['400'] }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchValue && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={handleClearSearch}
                                            disabled={isLoading}
                                            edge="end"
                                            sx={{
                                                color: themeColors['400'],
                                                '&:hover': {
                                                    color: themeColors['600'],
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    fontSize: '1rem',
                                    '& fieldset': {
                                        borderColor: semanticColors.border.light,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: themeColors['300'],
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: themeColors['400'],
                                        borderWidth: 2,
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    },
                                },
                            }}
                        />

                        {/* Кнопка "Найти" - на мобильных только иконка */}
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={isLoading}
                            sx={{
                                borderRadius: 2,
                                backgroundColor: themeColors['500'],
                                color: 'white',
                                px: isMobile ? 2 : 3,
                                py: 1.5,
                                minWidth: isMobile ? 'auto' : 'auto',
                                width: isMobile ? '48px' : 'auto',
                                '&:hover': {
                                    backgroundColor: themeColors['600']
                                },
                                '&:disabled': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                    color: 'rgba(0, 0, 0, 0.26)',
                                },
                            }}
                        >
                            {isMobile ? <SearchIcon /> : 'Найти'}
                        </Button>

                        {/* Кнопка поделиться */}
                        {hasActiveFilters && (
                            <ShareButton disabled={isLoading} />
                        )}
                    </Grid>

                    {/* Кнопка экспорта */}
                    <Grid item xs={12} md={4} lg={2} display='flex' justifyContent='end'>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={isExporting ? <GearLoader size={20} /> : <DownloadIcon />}
                            onClick={handleExport}
                            disabled={isLoading || isExporting || !data?.items?.length}
                            sx={{
                                borderRadius: 2,
                                backgroundColor: themeColors['500'],
                                color: 'white',
                                fontSize: 12,
                                py: 1.5,
                                '&:hover': { backgroundColor: themeColors['600'] },
                                '&:disabled': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                    color: 'rgba(0, 0, 0, 0.26)',
                                },
                            }}
                        >
                            {isExporting ? 'Экспорт...' : `Экспорт ${selectedRows.length > 0 ? `(${selectedRows.length})` : ''}`}
                        </Button>
                    </Grid>
                </Grid>

                {/* Информация о товарах и категориях */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    position: 'relative'
                }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GearLoader size={16} />
                            <Typography variant="body2" color="text.secondary">
                                Загрузка...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                {data?.total ? `Найдено товаров: ${data.total}` : 'Нет данных'}
                            </Typography>

                            {/* Прогресс рендеринга */}
                            {isRendering && (
                                <>
                                    <Typography variant="body2" color="text.secondary">•</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Загружено: {renderedItems.length} из {data?.total || 0}
                                    </Typography>
                                </>
                            )}

                            {sortedCategories.length > 0 && (
                                <>
                                    <Typography variant="body2" color="text.secondary">•</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Категорий: {sortedCategories.length}
                                    </Typography>
                                </>
                            )}
                        </>
                    )}

                    {data?.category && (
                        <>
                            <Typography variant="body2" color="text.secondary">•</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Категория: {data.category.title}
                            </Typography>
                        </>
                    )}

                    {shoppingListItems.length > 0 && (
                        <>
                            <Typography variant="body2" color="text.secondary">•</Typography>
                            <Chip
                                label={`В списке покупок: ${shoppingListItems.length}`}
                                size="small"
                                sx={{
                                    backgroundColor: themeColors['100'],
                                    color: themeColors['700'],
                                }}
                            />
                        </>
                    )}

                    {/* Прогресс-бар для рендеринга */}
                    {isRendering && (
                        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                            <LinearProgress
                                variant="determinate"
                                value={renderingProgress}
                                sx={{
                                    height: 3,
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: themeColors['500'],
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Таблица */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    backgroundColor: semanticColors.glass.background,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${semanticColors.border.light}`,
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: themeColors['200'],
                        borderRadius: 4,
                    },
                }}
            >
                {isLoading ? (
                    <Box sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                            <GearLoader size={32} />
                            <Typography sx={{ ml: 2 }}>Загрузка данных...</Typography>
                        </Box>
                        {[...Array(5)].map((_, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Skeleton variant="circular" width={50} height={50} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton width="60%" />
                                    <Skeleton width="40%" />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <>
                        {data?.items && data.items.length > 0 ? (
                            <Table sx={{ minWidth: 900 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={allSelected}
                                                onChange={handleSelectAll}
                                                disabled={isLoading}
                                                sx={{ color: themeColors['500'] }}
                                            />
                                        </TableCell>
                                        <TableCell>Фото</TableCell>
                                        <TableCell>Код</TableCell>
                                        <TableCell sx={{ minWidth: 250 }}>Название</TableCell>
                                        <TableCell align="center">Опт</TableCell>
                                        <TableCell align="center">Опт-Мастер</TableCell>
                                        <TableCell align="center" sx={{ minWidth: 120 }}>Наличие</TableCell>
                                        <TableCell align="center" sx={{ minWidth: 80 }}>Список</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableRows}

                                    {/* Индикатор загрузки для оставшихся товаров */}
                                    {isRendering && renderedItems.length < (data?.items?.length || 0) && (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                                    <GearLoader size={24} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Загружается еще {(data?.items?.length || 0) - renderedItems.length} товаров...
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mt: 2, maxWidth: 300, mx: 'auto' }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={renderingProgress}
                                                        sx={{
                                                            height: 6,
                                                            borderRadius: 3,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                            '& .MuiLinearProgress-bar': {
                                                                backgroundColor: themeColors['500'],
                                                                borderRadius: 3,
                                                            },
                                                        }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                        {Math.round(renderingProgress)}% завершено
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        ) : (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography color="text.secondary">
                                    Нет данных для отображения
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </TableContainer>

            {/* Popover для складов */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        backgroundColor: semanticColors.glass.background,
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${semanticColors.border.light}`,
                    }
                }}
            >
                <Box sx={{ p: 2, minWidth: 250 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{
                        color: themeColors['800'],
                        fontWeight: 600
                    }}>
                        Наличие по складам:
                    </Typography>
                    <List dense>
                        {selectedWarehouses?.map((warehouse, index) => (
                            <ListItem key={index} sx={{ px: 0 }}>
                                <ListItemText
                                    primary={warehouse.sklad_title}
                                    secondary={`${warehouse.count} шт.`}
                                    primaryTypographyProps={{
                                        color: themeColors['700'],
                                        fontSize: '0.9rem',
                                    }}
                                    secondaryTypographyProps={{
                                        color: warehouse.count > 0 ? themeColors['600'] : themeColors['400'],
                                        fontWeight: warehouse.count > 0 ? 500 : 400,
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Popover>

            {/* Modal для просмотра изображения */}
            <Modal
                open={Boolean(previewImage)}
                onClose={handleClosePreview}
                closeAfterTransition
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: 2,
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                        p: 2,
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        outline: 'none',
                    }}
                >
                    <IconButton
                        onClick={handleClosePreview}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {previewImage && (
                        <Box
                            component="img"
                            src={previewImage}
                            alt="Preview"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: 'calc(90vh - 80px)',
                                objectFit: 'contain',
                                borderRadius: 2,
                            }}
                        />
                    )}
                </Box>
            </Modal>

            {data && (
                <Box sx={{ mt: 3 }}>
                    <Pagination
                        totalItems={data.total}
                        currentPage={data.current_page}
                        lastPage={data.last_page}
                        perPage={data.per_page}
                        isLoading={isLoading}
                    />
                </Box>
            )}
        </Box>
    );
};

export default PriceListTable;